/* eslint-disable @typescript-eslint/consistent-type-assertions */

import { isAllowed } from "components/PermissionCheck/PermissionCheck";
import pluginRegistry, { ActionScope } from "components/Actions/pluginRegistry";
import ActionTemplateSearchResource from "client/resources/actionTemplateSearchResource";
import { Permission, ProjectResource, TenantedDeploymentMode, StartTrigger } from "client/resources";
import { useProjectContext } from "areas/projects/context";
import React from "react";
import ProcessListItem from "./ProcessListItem";
import getActionLogoUrl from "../../getActionLogoUrl";
import SpecialVariables from "client/specialVariables";
import Chip from "components/Chips/Chip";
import { useProcessContext } from "../Contexts/ProcessContext";
import { StoredStep, StoredAction } from "../types";
import { ProcessListLayoutLoaderLookupData } from "../ProcessListLayoutLoader";
import { useProcessErrorSelectors } from "../Contexts/ProcessErrors/ProcessErrorsContext";
import { convertProcessTypeToActionScope } from "../Common/CommonProcessHelpers";
import { useProcessWarningSelectors } from "../Contexts/ProcessWarnings/ProcessWarningsContext";
import { useKeyedItemAccess } from "../../../../../components/KeyAccessProvider/KeyedItemAccessProvider";
import Markdown from "../../../../../components/Markdown";

const processListItemStyles = require("./ProcessListItem.less");

function getTags(action: StoredAction, project: ProjectResource): string[] {
    if (project.TenantedDeploymentMode === TenantedDeploymentMode.Untenanted) {
        return [];
    }
    return action.TenantTags;
}

interface ProcessListPageListItemForActionProps {
    lookups: ProcessListLayoutLoaderLookupData;
    actionTemplates: ActionTemplateSearchResource[];
    step: StoredStep;
    action: StoredAction;
    actionIndex: number;
    stepIndex?: number;
}

const ProcessListPageListItemForAction: React.FC<ProcessListPageListItemForActionProps> = ({ lookups, actionTemplates, step, action, stepIndex }) => {
    const processContext = useProcessContext();
    const scope = convertProcessTypeToActionScope(processContext.selectors.getProcessType());
    const {
        state: { model: project, branch },
    } = useProjectContext();
    const { selectors } = useProcessContext();

    const isChildAction = !!stepIndex;
    let actionTypeName = action.ActionType;
    const actionTemplate = actionTemplates.find((x) => x.Type === action.ActionType);
    if (actionTemplate) {
        actionTypeName = actionTemplate.Name;
    }
    const stepNumber = selectors.getStepNumber(step.Id);
    const actionNumber = selectors.getActionNumber(action.Id);
    const processErrorSelectors = useProcessErrorSelectors();
    const errors = processErrorSelectors.getActionErrors(action.Id, selectors);
    const processWarningSelectors = useProcessWarningSelectors();
    const warnings = processWarningSelectors.getActionWarnings(action.Id, selectors);

    const feedViewPermissionGranted = isAllowed({ permission: Permission.FeedView, project: processContext.state.model.process?.ProjectId, wildcard: true });
    const environmentsByNameOrId = lookups.environmentsByNameOrId;
    const channelsByNameOrId = lookups.channelsByNameOrId;

    return (
        <ProcessListItem
            actionType={actionTypeName}
            logoUrl={getActionLogoUrl(action)}
            environmentsLookup={
                environmentsByNameOrId
                    ? action.Environments.map((key) => ({
                          Key: key,
                          Environment: environmentsByNameOrId[key],
                      }))
                    : undefined
            }
            excludedEnvironmentsLookup={
                environmentsByNameOrId
                    ? action.ExcludedEnvironments.map((key) => ({
                          Key: key,
                          Environment: environmentsByNameOrId[key],
                      }))
                    : undefined
            }
            channelsLookup={
                channelsByNameOrId
                    ? action.Channels.map((key) => ({
                          Key: key,
                          Channel: channelsByNameOrId[key],
                      }))
                    : undefined
            }
            tags={getTags(action, project)}
            isDisabled={selectors.isActionDisabled(action.Id)}
            notes={action.Notes}
            actionErrors={errors}
            actionWarnings={warnings}
            index={`${isChildAction ? `${stepNumber}.${actionNumber}.` : `${stepNumber}.`}`}
            detailsUrl={selectors.getStepDetailsUrl(project.Slug, branch, step.Id, action.Id)}
            isParentGroup={false}
            name={action.Name}
            isRunInParallelWithLast={step.StartTrigger === StartTrigger.StartWithPrevious && !selectors.isChildAction(action.Id) && !selectors.isFirstStep(step.Id)}
        >
            <div className={processListItemStyles.summaryContainer}>
                {pluginRegistry
                    .getAction(action.ActionType, scope)
                    .summary(
                        action.Properties,
                        isChildAction ? "" : (step.Properties[SpecialVariables.Action.TargetRoles] as string),
                        action.Packages,
                        action.WorkerPoolId ? lookups.workerPoolsByNameOrId[action.WorkerPoolId] : undefined,
                        action.WorkerPoolVariable ?? undefined
                    )}
                {action.Container.Image && action.Container.FeedId && action.Properties["Octopus.Action.RunOnServer"] === "true" ? (
                    <span style={{ paddingLeft: "0.1em" }}>
                        <Chip description={feedViewPermissionGranted ? `Container Image: ${action.Container.Image}` : undefined}>Runs in a container</Chip>
                    </span>
                ) : (
                    ""
                )}
            </div>
        </ProcessListItem>
    );
};

export default ProcessListPageListItemForAction;
